// Course-setup presentation logic shared by LessonsView and CourseSetupWizard.

/**
 * Decides how a pending COLLECT_BACKGROUND course stage is presented, from the
 * dialog's persisted meta. Pure so the flash-regression cases stay unit-tested:
 * a learner who opted into the conversational interview must land in the chat
 * directly — never see the wizard flash open and close.
 *
 * @param {object|null} meta - dialog-meta snapshot value
 * @returns {"none"|"chat"|"wizard"}
 */
export function resolveBackgroundPresentation(meta) {
  // Missing meta means the course stage points at a deleted/broken dialog:
  // opening a wizard for it would only produce a failing submit.
  if (!meta) return "none";
  // Dialog already finished: course metadata hasn't caught up yet, show nothing.
  if (meta.completedAt) return "none";
  // Learner previously chose the conversational interview.
  if (meta.skippedStructuredInput) return "chat";
  return "wizard";
}

/**
 * localStorage key for a course-setup draft. Shared by the wizard (autosave /
 * restore) and LessonsView (cleanup after a successful switch to chat).
 *
 * Returns null while there is no dialog id. The optimistic mount renders the
 * wizard BEFORE the server has minted one, and `localStorage.setItem` coerces
 * a null key to the string "null" — one shared bucket every id-less session
 * would read and write, leaking one learner's answers into the next. The
 * draft helpers guard on the id too; this is the second line of defence.
 *
 * @param {string|null|undefined} dialogId
 * @returns {string|null}
 */
export function courseSetupDraftKey(dialogId) {
  if (!dialogId) return null;
  return `courseSetupDraft:${dialogId}`;
}
